import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Lock } from 'lucide-react';
import { updatePassword } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '@/shared/lib/firebase';
import { useToast } from '@/shared/hooks/use-toast';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthFormLayout } from '../layouts/AuthFormLayout';
import { PasswordInput } from '../components/PasswordInput';
import { LoadingButton } from '../components/LoadingButton';

const PASSWORD_TESTS = [
  { key: 'length',    test: (p: string) => p.length >= 8 },
  { key: 'uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number',    test: (p: string) => /\d/.test(p) },
  { key: 'special',   test: (p: string) => /[!@#$%^&*()_+\-=[\]{}';":"|,.<>/?]/.test(p) },
] as const;

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!auth.currentUser) navigate('/forgot-password', { replace: true });
  }, [navigate]);

  const allPasswordReqsMet = PASSWORD_TESTS.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword;
  const canSubmit = allPasswordReqsMet && passwordsMatch && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !auth.currentUser) return;
    setSubmitting(true);
    try {
      await updatePassword(auth.currentUser, password);
      toast({ title: t('auth.resetPassword.success'), description: t('auth.resetPassword.successDesc') });
      navigate('/app', { replace: true });
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const description =
        code === 'auth/requires-recent-login'
          ? t('auth.resetPassword.errors.requiresReauth')
          : t('auth.resetPassword.errors.failed');
      toast({ title: t('common.error'), description, variant: 'destructive' });
      if (code === 'auth/requires-recent-login') navigate('/forgot-password', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout showBranding={false}>
      <AuthFormLayout
        title={t('auth.resetPassword.title')}
        subtitle={t('auth.resetPassword.subtitle')}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-accent shrink-0" />
              <p className="text-sm font-medium">{t('auth.completeProfile.passwordSection')}</p>
            </div>

            <PasswordInput
              id="rp-password"
              label={t('auth.resetPassword.newPassword')}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              required
            />

            {password && (
              <div className="space-y-1 pt-1">
                {PASSWORD_TESTS.map((req) => {
                  const met = req.test(password);
                  return (
                    <div key={req.key} className="flex items-center gap-2 text-xs">
                      <CheckCircle className={`w-3.5 h-3.5 ${met ? 'text-success' : 'text-muted-foreground/40'}`} />
                      <span className={met ? 'text-success' : 'text-muted-foreground'}>
                        {t(`auth.register.passwordReqs.${req.key}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <PasswordInput
              id="rp-confirm"
              label={t('auth.completeProfile.confirmPassword')}
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive">{t('auth.completeProfile.passwordMismatch')}</p>
            )}
          </div>

          <LoadingButton
            type="submit"
            isLoading={submitting}
            loadingText={t('auth.resetPassword.submitting')}
            disabled={!canSubmit}
          >
            {t('auth.resetPassword.submit')}
          </LoadingButton>
        </form>
      </AuthFormLayout>
    </AuthLayout>
  );
};

export default ResetPassword;
